/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import {
  Table,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Button,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Breadcrumb,
} from "antd";
import { AiOutlineUpload } from "react-icons/ai";
import { Link } from "react-router-dom";

import {
  getMenuAll,
  createMenu,
  updateMenu,
  deleteMenu,
  getPages,
  uploadFile,
} from "../../../services/cms";

const { Option } = Select;

const GROUPS = [
  { value: "primary-boards", label: "Board tabs (CICSE / PYP / CAIE)" },
  { value: "main-menu", label: "Main menu (waffle)" },
  { value: "footer", label: "Footer" },
];

const CmsNavigation = () => {
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [type, setType] = useState("internal");
  const [group, setGroup] = useState("primary-boards");
  const [logoFile, setLogoFile] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getMenuAll();
      setRows(data.menu || []);
    } catch (e) {
      message.error("Failed to load navigation");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    getPages({ viewSize: 200 })
      .then((d) => setPages(d.pages || []))
      .catch(() => {});
  }, []);

  const labelById = (id) => {
    const found = rows.find((r) => r._id === id);
    return found ? found.label : "—";
  };

  const openAdd = () => {
    setEditingId(null);
    setType("internal");
    setGroup("primary-boards");
    setLogoFile(null);
    setDocFile(null);
    setLogoUrl("");
    setFileUrl("");
    form.resetFields();
    form.setFieldsValue({
      group: "primary-boards",
      type: "internal",
      order: 0,
      openInNewTab: false,
      isActive: true,
    });
    setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r._id);
    setType(r.type || "internal");
    setGroup(r.group || "primary-boards");
    setLogoFile(null);
    setDocFile(null);
    setLogoUrl((r.logo && r.logo.url) || "");
    setFileUrl(r.fileUrl || "");
    form.setFieldsValue({
      label: r.label,
      group: r.group || "main-menu",
      parent: r.parent || undefined,
      type: r.type || "internal",
      page: (r.page && (r.page._id || r.page)) || undefined,
      url: r.url || "",
      order: r.order || 0,
      logoAlt: (r.logo && r.logo.alt) || "",
      openInNewTab: !!r.openInNewTab,
      isActive: r.isActive !== false,
    });
    setModalOpen(true);
  };

  const onSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }
    setSaving(true);
    try {
      let logo = logoUrl;
      let doc = fileUrl;
      if (logoFile) logo = (await uploadFile(logoFile)).url;
      if (docFile) doc = (await uploadFile(docFile)).url;

      const payload = {
        label: values.label,
        group: values.group,
        parent: values.parent || null,
        type: values.type,
        page: values.type === "page" ? values.page : null,
        url:
          values.type === "internal" || values.type === "external"
            ? values.url || ""
            : "",
        fileUrl: values.type === "file" ? doc || "" : "",
        logo: { url: logo || "", alt: values.logoAlt || "" },
        order: values.order || 0,
        openInNewTab: !!values.openInNewTab,
        isActive: values.isActive !== false,
      };
      if (editingId) await updateMenu(editingId, payload);
      else await createMenu(payload);
      message.success(`Menu item ${editingId ? "updated" : "created"}`);
      setModalOpen(false);
      load();
    } catch (e) {
      message.error(
        (e &&
          e.response &&
          e.response.data &&
          e.response.data.error &&
          e.response.data.error.message) ||
          "Save failed"
      );
    }
    setSaving(false);
  };

  const onDelete = async (id) => {
    try {
      await deleteMenu(id);
      message.success("Deleted (including any sub-items)");
      load();
    } catch (e) {
      message.error("Delete failed");
    }
  };

  // Parent options: items in the chosen group, excluding the item being edited.
  const parentOptions = rows.filter(
    (r) => r.group === group && r._id !== editingId
  );

  const columns = [
    { title: "Label", dataIndex: "label" },
    {
      title: "Group",
      dataIndex: "group",
    },
    {
      title: "Parent",
      dataIndex: "parent",
      render: (p) => (p ? labelById(p) : "—"),
    },
    { title: "Type", dataIndex: "type" },
    {
      title: "Target",
      render: (_, r) =>
        r.type === "page"
          ? `page: ${(r.page && (r.page.slug || r.page)) || ""}`
          : r.type === "file"
          ? "file"
          : r.url,
    },
    { title: "Order", dataIndex: "order" },
    {
      title: "Active",
      dataIndex: "isActive",
      render: (a) => (a === false ? "No" : "Yes"),
    },
    {
      title: "Actions",
      render: (_, r) => (
        <div className="flex gap-4">
          <a onClick={() => openEdit(r)}>Edit</a>
          <Popconfirm title="Delete this item and its sub-items?" onConfirm={() => onDelete(r._id)}>
            <a style={{ color: "#e3342f" }}>Delete</a>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="content-panel px-4">
      <div className="mx-4">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <Link to="/dashboard">Home</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Navigation Menu</Breadcrumb.Item>
        </Breadcrumb>
        <div className="flex justify-between">
          <h1 className="page-heading">Navigation Menu</h1>
          <button
            onClick={openAdd}
            className="bg-[#FA6210] h-12 px-5 font-semibold hover:bg-[#15538B] rounded-md text-white"
          >
            Add Menu Item
          </button>
        </div>
        <div className="border-b -mt-4 mb-8" />
        <Table
          rowKey="_id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={false}
        />
      </div>

      <Modal
        title={editingId ? "Edit Menu Item" : "Add Menu Item"}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        confirmLoading={saving}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="label"
            label="Label"
            rules={[{ required: true, message: "Label is required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="group" label="Group">
            <Select
              onChange={(v) => {
                setGroup(v);
                form.setFieldsValue({ parent: undefined });
              }}
            >
              {GROUPS.map((g) => (
                <Option key={g.value} value={g.value}>
                  {g.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="parent" label="Parent (leave empty for top level)">
            <Select allowClear placeholder="— top level —">
              {parentOptions.map((p) => (
                <Option key={p._id} value={p._id}>
                  {p.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="type" label="Links to">
            <Select onChange={(v) => setType(v)}>
              <Option value="page">CMS page</Option>
              <Option value="internal">Internal route</Option>
              <Option value="external">External link</Option>
              <Option value="file">File / PDF</Option>
            </Select>
          </Form.Item>

          {type === "page" && (
            <Form.Item name="page" label="CMS page">
              <Select
                showSearch
                optionFilterProp="children"
                placeholder="Select a page"
              >
                {pages.map((p) => (
                  <Option key={p._id} value={p._id}>
                    {p.title} (/{p.slug})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          {(type === "internal" || type === "external") && (
            <Form.Item
              name="url"
              label={type === "external" ? "External URL" : "Internal path"}
              extra={type === "internal" ? "e.g. /about" : "e.g. https://..."}
            >
              <Input />
            </Form.Item>
          )}
          {type === "file" && (
            <Form.Item label="File / PDF">
              {fileUrl ? (
                <div className="mb-2 text-xs text-gray-500 break-all">
                  Current: {fileUrl}
                </div>
              ) : null}
              <Upload
                beforeUpload={(f) => {
                  setDocFile(f);
                  return false;
                }}
                maxCount={1}
                accept=".pdf,.doc,.docx,image/*"
              >
                <Button icon={<AiOutlineUpload />}>Select file</Button>
              </Upload>
            </Form.Item>
          )}

          <Form.Item label="Logo (board tabs only, optional)">
            {logoUrl ? (
              <div className="mb-2 text-xs text-gray-500 break-all">
                Current: {logoUrl}
              </div>
            ) : null}
            <Upload
              beforeUpload={(f) => {
                setLogoFile(f);
                return false;
              }}
              maxCount={1}
              accept="image/*"
            >
              <Button icon={<AiOutlineUpload />}>Select logo</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="logoAlt" label="Logo alt text">
            <Input />
          </Form.Item>

          <Form.Item name="order" label="Order">
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item name="openInNewTab" label="Open in new tab" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CmsNavigation;
